import {
  createSlice,
  createSelector,
  createAsyncThunk,
  PayloadAction,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { camelizeKeys } from "humps";
import { stringify } from "qs";
import { createWrapper } from "main/javascripts/api/AxiosWrapper";
import { ErrorResponse } from "main/javascripts/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "main/javascripts/utils/sliceUtil";
import { RootState } from "main/javascripts/store";
import { LpCruise } from "main/javascripts/types/lpCruise";
import { Cruise } from "main/javascripts/types/cruise";
import { Region } from "main/javascripts/types/region";
import { fetchCruises } from "../../cruise/cruiseSlice";

const key = "lpCruise";
const adapter = createEntityAdapter<Cruise.Cruise>();

const initialState = adapter.getInitialState({
  vesselBrand: null,
  vessel: null,
  vessels: [],
  region: null,
  regions: [],
  descendingRegions: [],
  parentRegions: [],
  sales: [],
  asiaCruises: [],
  japanCruises: [],
  mediterraneanCruises: [],
  usaCruises: [],
  europeCruises: [],
  oceaniaCruises: [],
  breadcrumbs: null,
  page: 0,
  numPerPage: 10,
  totalNum: 0,
  loading: false,
  errors: null,
});

/** Async **/
export const fetchLpCruiseDstData = createAsyncThunk<
  {
    region: LpCruise.Region;
    cruises: Cruise.Cruise[];
    sales: Cruise.SaleCruise[];
    vessels: LpCruise.Vessel[];
    descendingRegions: Region.Region[];
    parentRegions: Region.Region[];
    breadcrumbs: any;
    metatitle: string;
    metadescription: string;
    totalNum: number;
    page: number;
    numPerPage: number;
  },
  {
    id: number;
    params?: any;
    isServerSide?: boolean;
    headers?: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchLpCruiseDstData`, async (args, { rejectWithValue }) => {
  try {
    const { id, params, isServerSide = false, headers } = args;
    const paramString = stringify(params, { arrayFormat: "indices" });
    const url = `/api/page/lp/cruises/dst_regions/${id}.json?${paramString}`;
    const result = await createWrapper({ isServerSide }).get(url, {
      headers,
    });
    return {
      region: result.data.region,
      cruises: result.data.cruises,
      sales: result.data.sales,
      vessels: result.data.vessels,
      descendingRegions: result.data.descendingRegions,
      parentRegions: result.data.parentRegions,
      breadcrumbs: result.data.breadcrumbs,
      metatitle: result.data.metatitle,
      metadescription: result.data.metadescription,
      totalNum: result.data.totalNum,
      page: result.data.page,
      numPerPage: result.data.numPerPage,
    };
  } catch (err) {
    return rejectWithValue(camelizeKeys(err.response.data));
  }
});

export const fetchLpVesselBrandData = createAsyncThunk<
  {
    vesselBrand: LpCruise.VesselBrand;
    vessels: LpCruise.Vessel[];
    sales: Cruise.SaleCruise[];
    metatitle: string;
    metadescription: string;
  },
  {
    code: string;
    params?: any;
    isServerSide?: boolean;
    headers?: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchLpVesselBrandData`, async (args, { rejectWithValue }) => {
  try {
    const { code, params, isServerSide = false, headers } = args;
    const paramString = stringify(params, { arrayFormat: "indices" });
    const url = `/api/lp/vessel_brands/${code}.json?${paramString}`;
    const result = await createWrapper({ isServerSide }).get(url, {
      headers,
    });
    return {
      vesselBrand: result.data.vesselBrand,
      vessels: result.data.vessels,
      sales: result.data.sales,
      metatitle: result.data.metatitle,
      metadescription: result.data.metadescription,
    };
  } catch (err) {
    return rejectWithValue(camelizeKeys(err.response.data));
  }
});

export const fetchLpVesselData = createAsyncThunk<
  {
    vessel: LpCruise.Vessel;
    cruises: Cruise.Cruise[];
    sales: Cruise.SaleCruise[];
    regions: Region.Region[];
    breadcrumbs: any;
    totalNum: number;
    page: number;
    numPerPage: number;
  },
  {
    code: string;
    params?: any;
    isServerSide?: boolean;
    headers?: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchLpVesselData`, async (args, { rejectWithValue }) => {
  try {
    const { code, params, isServerSide = false, headers } = args;
    const paramString = stringify(params, { arrayFormat: "indices" });
    const url = `/api/lp/vessels/${code}.json?${paramString}`;
    const result = await createWrapper({ isServerSide }).get(url, {
      headers,
    });
    return {
      vessel: result.data.vessel,
      cruises: result.data.cruises,
      sales: result.data.sales,
      regions: result.data.regions,
      breadcrumbs: result.data.breadcrumbs,
      totalNum: result.data.totalNum,
      page: result.data.page,
      numPerPage: result.data.numPerPage,
    };
  } catch (err) {
    return rejectWithValue(camelizeKeys(err.response.data));
  }
});

export const fetchLpCruiseData = createAsyncThunk<
  {
    asiaCruises: Cruise.Cruise[];
    japanCruises: Cruise.Cruise[];
    mediterraneanCruises: Cruise.Cruise[];
    usaCruises: Cruise.Cruise[];
    europeCruises: Cruise.Cruise[];
    oceaniaCruises: Cruise.Cruise[];
    sales: Cruise.SaleCruise[];
  },
  {
    params?: any;
    isServerSide?: boolean;
    headers?: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchLpCruiseData`, async (args, { rejectWithValue }) => {
  try {
    const { params, isServerSide = false, headers } = args;
    const paramString = stringify(params, { arrayFormat: "indices" });
    const url = `/api/lp/cruises.json?${paramString}`;
    const result = await createWrapper({ isServerSide }).get(url, {
      headers,
    });
    return {
      asiaCruises: result.data.asiaCruises,
      japanCruises: result.data.japanCruises,
      mediterraneanCruises: result.data.mediterraneanCruises,
      usaCruises: result.data.usaCruises,
      europeCruises: result.data.europeCruises,
      oceaniaCruises: result.data.oceaniaCruises,
      sales: result.data.sales,
    };
  } catch (err) {
    return rejectWithValue(camelizeKeys(err.response.data));
  }
});

export const fetchLpOverseasCruiseData = createAsyncThunk<
  {
    asiaCruises: Cruise.Cruise[];
    mediterraneanCruises: Cruise.Cruise[];
    usaCruises: Cruise.Cruise[];
    europeCruises: Cruise.Cruise[];
    oceaniaCruises: Cruise.Cruise[];
    sales: Cruise.SaleCruise[];
  },
  {
    params?: any;
    isServerSide?: boolean;
    headers?: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchLpOverseasCruiseData`, async (args, { rejectWithValue }) => {
  try {
    const { params, isServerSide = false, headers } = args;
    const paramString = stringify(params, { arrayFormat: "indices" });
    const url = `/api/lp/oversea_cruises.json?${paramString}`;
    const result = await createWrapper({ isServerSide }).get(url, {
      headers,
    });
    return {
      asiaCruises: result.data.asiaCruises,
      mediterraneanCruises: result.data.mediterraneanCruises,
      usaCruises: result.data.usaCruises,
      europeCruises: result.data.europeCruises,
      oceaniaCruises: result.data.oceaniaCruises,
      sales: result.data.sales || [],
    };
  } catch (err) {
    return rejectWithValue(camelizeKeys(err.response.data));
  }
});

/** slice **/
export const lpCruiseSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    initLpCruiseDst: (
      state,
      action: PayloadAction<{
        region: LpCruise.Region;
        cruises: Cruise.Cruise[];
        vessels: Cruise.Vessel[];
        sales: any[];
        descendingRegions: Region.Region[];
        parentRegions: Region.Region[];
        breadcrumbs: any;
        page: number;
        numPerPage: number;
        totalNum: number;
      }>
    ) => {
      adapter.setAll(state, action.payload.cruises);
      state.vessels = action.payload.vessels;
      state.region = action.payload.region;
      state.sales = action.payload.sales;
      state.descendingRegions = action.payload.descendingRegions;
      state.parentRegions = action.payload.parentRegions;
      state.breadcrumbs = action.payload.breadcrumbs;
      state.page = action.payload.page;
      state.numPerPage = action.payload.numPerPage;
      state.totalNum = action.payload.totalNum;
    },
    initLpVesselBrand: (
      state,
      action: PayloadAction<{
        vesselBrand: LpCruise.VesselBrand;
        vessels: LpCruise.Vessel[];
        sales: any[];
        metatitle: string;
        metadescription: string;
      }>
    ) => {
      state.vesselBrand = action.payload.vesselBrand;
      state.vessels = action.payload.vessels;
      state.sales = action.payload.sales;
      // state.metatitle = action.payload.metatitle;
      // state.metadescription = action.payload.metadescription;
    },
    initLpVessel: (
      state,
      action: PayloadAction<{
        vessel: LpCruise.Vessel;
        cruises: Cruise.Cruise[];
        sales: any[];
        regions: Region.Region[];
        breadcrumbs: any;
        page: number;
        numPerPage: number;
        totalNum: number;
      }>
    ) => {
      adapter.setAll(state, action.payload.cruises);
      state.vessel = action.payload.vessel;
      state.regions = action.payload.regions;
      state.sales = action.payload.sales;
      state.breadcrumbs = action.payload.breadcrumbs;
      state.page = action.payload.page;
      state.numPerPage = action.payload.numPerPage;
      state.totalNum = action.payload.totalNum;
    },
    initLpCruise: (
      state,
      action: PayloadAction<{
        asiaCruises: Cruise.Cruise[];
        japanCruises: Cruise.Cruise[];
        mediterraneanCruises: Cruise.Cruise[];
        usaCruises: Cruise.Cruise[];
        europeCruises: Cruise.Cruise[];
        oceaniaCruises: Cruise.Cruise[];
        sales: Cruise.SaleCruise[];
      }>
    ) => {
      state.asiaCruises = action.payload.asiaCruises;
      state.japanCruises = action.payload.japanCruises;
      state.mediterraneanCruises = action.payload.mediterraneanCruises;
      state.usaCruises = action.payload.usaCruises;
      state.europeCruises = action.payload.europeCruises;
      state.oceaniaCruises = action.payload.oceaniaCruises;
      state.sales = action.payload.sales;
    },
    initLpOverseasCruise: (
      state,
      action: PayloadAction<{
        asiaCruises: Cruise.Cruise[];
        mediterraneanCruises: Cruise.Cruise[];
        usaCruises: Cruise.Cruise[];
        europeCruises: Cruise.Cruise[];
        oceaniaCruises: Cruise.Cruise[];
        sales: Cruise.SaleCruise[];
      }>
    ) => {
      state.asiaCruises = action.payload.asiaCruises;
      state.mediterraneanCruises = action.payload.mediterraneanCruises;
      state.usaCruises = action.payload.usaCruises;
      state.europeCruises = action.payload.europeCruises;
      state.oceaniaCruises = action.payload.oceaniaCruises;
      state.sales = action.payload.sales;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLpCruiseDstData.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.cruises);
        state.vessels = action.payload.vessels;
        state.region = action.payload.region;
        state.descendingRegions = action.payload.descendingRegions;
        state.parentRegions = action.payload.parentRegions;
        state.sales = action.payload.sales;
        state.breadcrumbs = action.payload.breadcrumbs;
        state.totalNum = action.payload.totalNum;
        state.page = isNaN(Number(action.payload.page))
          ? 0
          : Number(action.payload.page);
        state.numPerPage = action.payload.numPerPage;
      })
      .addCase(fetchLpVesselBrandData.fulfilled, (state, action) => {
        state.vesselBrand = action.payload.vesselBrand;
        state.vessels = action.payload.vessels;
        state.sales = action.payload.sales;
      })
      .addCase(fetchLpVesselData.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.cruises);
        state.vessel = action.payload.vessel;
        state.regions = action.payload.regions;
        state.sales = action.payload.sales;
        state.breadcrumbs = action.payload.breadcrumbs;
        state.totalNum = action.payload.totalNum;
        state.page = isNaN(Number(action.payload.page))
          ? 0
          : Number(action.payload.page);
        state.numPerPage = action.payload.numPerPage;
      })
      .addCase(fetchLpCruiseData.fulfilled, (state, action) => {
        state.asiaCruises = action.payload.asiaCruises;
        state.japanCruises = action.payload.japanCruises;
        state.mediterraneanCruises = action.payload.mediterraneanCruises;
        state.usaCruises = action.payload.usaCruises;
        state.europeCruises = action.payload.europeCruises;
        state.oceaniaCruises = action.payload.oceaniaCruises;
        state.sales = action.payload.sales;
      })
      .addCase(fetchLpOverseasCruiseData.fulfilled, (state, action) => {
        state.asiaCruises = action.payload.asiaCruises;
        state.mediterraneanCruises = action.payload.mediterraneanCruises;
        state.usaCruises = action.payload.usaCruises;
        state.europeCruises = action.payload.europeCruises;
        state.oceaniaCruises = action.payload.oceaniaCruises;
        state.sales = action.payload.sales;
      })
      .addCase(fetchCruises.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.cruises);
        state.totalNum = action.payload.totalNum;
        state.page = isNaN(Number(action.payload.page))
          ? 0
          : Number(action.payload.page);
        state.numPerPage = action.payload.numPerPage;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: cruiseByIdSelector,
  selectIds: cruiseIdsSelector,
  selectEntities: cruiseEntitiesSelector,
  selectAll: cruisesSelector,
  selectTotal: totalCruisesSelector,
} = adapter.getSelectors((state: RootState) => state.lpCruise);

const stateSelector = (state: { [key]: LpCruise.LpCruiseEntityState }) =>
  state[key];

export const vesselBrandSelector = createSelector(
  stateSelector,
  (state) => state.vesselBrand
);

export const vesselSelector = createSelector(
  stateSelector,
  (state) => state.vessel
);

export const vesselsSelector = createSelector(
  stateSelector,
  (state) => state.vessels
);

export const salesSelector = createSelector(
  stateSelector,
  (state) => state.sales
);

export const regionSelector = createSelector(
  stateSelector,
  (state) => state.region
);

export const regionsSelector = createSelector(
  stateSelector,
  (state) => state.regions
);

export const descendingRegionsSelector = createSelector(
  stateSelector,
  (state) => state.descendingRegions
);

export const parentRegionsSelector = createSelector(
  stateSelector,
  (state) => state.parentRegions
);

export const asiaCruisesSelector = createSelector(
  stateSelector,
  (state) => state.asiaCruises
);

export const japanCruisesSelector = createSelector(
  stateSelector,
  (state) => state.japanCruises
);

export const mediterraneanCruisesSelector = createSelector(
  stateSelector,
  (state) => state.mediterraneanCruises
);

export const usaCruisesSelector = createSelector(
  stateSelector,
  (state) => state.usaCruises
);

export const europeCruisesSelector = createSelector(
  stateSelector,
  (state) => state.europeCruises
);

export const oceaniaCruisesSelector = createSelector(
  stateSelector,
  (state) => state.oceaniaCruises
);

export const breadcrumbsSelector = createSelector(
  stateSelector,
  (state) => state.breadcrumbs
);

export const pageSelector = createSelector(
  stateSelector,
  (state) => state.page
);

export const numPerPageSelector = createSelector(
  stateSelector,
  (state) => state.numPerPage
);

export const totalNumSelector = createSelector(
  stateSelector,
  (state) => state.totalNum
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading
);

/** action export **/
export const {
  initLpCruiseDst,
  initLpVesselBrand,
  initLpVessel,
  initLpCruise,
  initLpOverseasCruise,
  setLoading,
} = lpCruiseSlice.actions;
